import { faFlag } from "@elynx/free-solid-svg-icons";
import { CellClassParams, CellStyle, ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import { GridColumnAggregateEnum } from "../../common/enums/GridColumnAggregateEnum";
import { getAggregateFunction } from "../ag-grid-extensions/AggregateHelper";
import { MemoCellRenderer } from "../ag-grid-extensions/renderers/MemoCellRenderer";
import ShortcutsCellRenderer from "../ag-grid-extensions/renderers/ShortcutsCellRenderer";
import { SparklineCellRenderer } from "../ag-grid-extensions/renderers/SparklineCellRenderer";
import StandardCellRenderer from "../ag-grid-extensions/renderers/StandardCellRenderer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { MemoCellRendererV2 } from "../ag-grid-extensions/renderers/MemoCellRendererV2";
import { memoTextComparator } from "../ag-grid-extensions/comparators/MemoTextComparator";

// NOTE: The use of new Function() in this file is an intentional optimization that
// provides flexibility. Eslint complains about the alleged "bad practice", so the
// warning is disabled throughout the file.

// TODO: This should probably be StandardColumnCreatorParams, or one for each column type, starting with a base
export type ColumnCreatorParams = {
    field: string;
    headerName: string;
    styler?: (params: CellClassParams) => CellStyle | null;
    initialSort?: 'asc' | 'desc' | null;
    initialSortIndex?: number;
    initialPinned?: boolean | 'left' | 'right';// | null;
    lockPinned?: boolean;
    lockPosition?: boolean;
    lockVisible?: boolean;
    footerAggregate?: GridColumnAggregateEnum;
    disableNavigation?: boolean;
    useDataValue?: boolean;
    suppressAutoResize?: boolean;
};


// If we're keeping sparklines then they'll have a derived coldef that contains extra members

// General purpose column creators

export function createTextColumn({
    field,
    headerName,
    styler,
    initialSort,
    initialSortIndex,
    initialPinned,
    lockPinned,
    lockPosition,
    lockVisible,
    footerAggregate,
    suppressAutoResize
}: ColumnCreatorParams): ColDef {

    return {
        headerName: headerName,
        cellRenderer: StandardCellRenderer,
        cellRendererParams: {
            cellDataGetter: (params: ICellRendererParams) => params.data?.[field]
        },
        cellStyle: styler,
        valueGetter: (params: ValueGetterParams) => params.data?.[field]?.displayText,

        // sort: initialSort ?? null,
        // sortIndex: initialSortIndex ?? null,
        initialSort: initialSort,
        initialSortIndex: initialSortIndex,
        //pinned: pinned ?? null,
        //pinned: initialPinned,
        initialPinned: initialPinned,
        lockPinned: lockPinned,
        lockPosition: lockPosition,
        lockVisible: lockVisible,
        aggFunc: getAggregateFunction(footerAggregate),
        sortable: true,
        field: field,
        type: "text",
        suppressAutoSize: suppressAutoResize ?? false
    };
}

export function createNumericColumn({
    field,
    headerName,
    styler,
    initialSort,
    initialSortIndex,
    initialPinned,
    lockPinned,
    lockPosition,
    lockVisible,
    footerAggregate
}: ColumnCreatorParams): ColDef {

    return {
        headerName: headerName,
        cellRenderer: StandardCellRenderer,
        cellRendererParams: {
            cellDataGetter: (params: ICellRendererParams) => params.data?.[field]
        },
        cellStyle: styler,
        valueGetter: (params: ValueGetterParams) => params.data?.[field]?.dataValue,
        // sort: initialSort ?? null,
        // sortIndex: initialSortIndex ?? null,
        initialSort: initialSort,
        initialSortIndex: initialSortIndex,
        //pinned: pinned ?? null,
        initialPinned: initialPinned,
        //pinned: initialPinned,
        lockPinned: lockPinned,
        lockPosition: lockPosition,
        lockVisible: lockVisible,
        aggFunc: getAggregateFunction(footerAggregate),
        sortable: true,
        //flex: 1,
        //wrapText: true,
        field: field,
        type: "number"
    };
}

export function createTimestampColumn({
    field,
    headerName,
    styler,
    initialSort,
    initialSortIndex,
    initialPinned,
    lockPinned,
    lockPosition,
    lockVisible,
    useDataValue
}: ColumnCreatorParams): ColDef {
    return {
        headerName: headerName,
        cellRenderer: StandardCellRenderer,
        cellRendererParams: {
            cellDataGetter: (params: ICellRendererParams) => params.data?.[field]
        },
        cellStyle: styler,
        valueGetter: (params: ValueGetterParams) => useDataValue ? params.data?.[field]?.displayText ? new Date(params.data?.[field]?.displayText) : null : params.data?.[field]?.timestamp,
        // sort: initialSort ?? null,
        // sortIndex: initialSortIndex ?? null,
        initialSort: initialSort,
        initialSortIndex: initialSortIndex,
        //pinned: pinned ?? null,
        initialPinned: initialPinned,
        //pinned: initialPinned,
        lockPinned: lockPinned,
        lockPosition: lockPosition,
        lockVisible: lockVisible,
        sortable: true,
        //flex: 1,
        //wrapText: true,
        field: field,
        aggFunc: null,
        type: "timestamp"
    };
}

// Specialized column creators

export function createRecentMemoColumn({
    field,
    headerName,
    initialPinned,
    lockPinned,
    lockPosition
}: ColumnCreatorParams): ColDef {
    return {
        field: field,
        headerName: headerName,
        cellRenderer: MemoCellRenderer,
        filter: undefined,
        initialPinned: initialPinned,
        //pinned: initialPinned,
        lockPinned: lockPinned,
        lockPosition: lockPosition,
        resizable: false,
        sortable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        width: 35,
        maxWidth: 35,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        headerComponentParams: {
            component:
                <Box>
                    <FontAwesomeIcon
                        icon={faFlag}
                        fixedWidth={true}
                        style={{
                            fontSize: "1em",
                            padding: 0
                        }}
                    />
                </Box>
        }
    };
}


export function createMemoColumnV2({
    field,
    headerName,
    styler,
    initialPinned,
    lockPinned,
    lockPosition
}: ColumnCreatorParams): ColDef {


        // headerName: headerName,
        // cellRenderer: StandardCellRenderer,
        // cellRendererParams: {
        //     // eslint-disable-next-line no-new-func
        //     //cellDataGetter: new Function("params", `return params.data?.${field};`) as (params: IStandardCellRendererParams) => StandardCellData,
        //     cellDataGetter: (params: BaseColDefParams) => params.data?.[field]
        // },
        // cellStyle: styler,
        // // eslint-disable-next-line no-new-func
        // // valueGetter: new Function("params", `return params.data?.${field}?.displayText;`) as (params: ValueGetterParams) => string,
        // valueGetter: (params: ValueGetterParams) => params.data?.[field]?.displayText,

        // // sort: initialSort ?? null,
        // // sortIndex: initialSortIndex ?? null,
        // initialSort: initialSort,
        // initialSortIndex: initialSortIndex,
        // //pinned: pinned ?? null,
        // //pinned: initialPinned,
        // initialPinned: initialPinned,
        // lockPinned: lockPinned,
        // lockPosition: lockPosition,
        // lockVisible: lockVisible,
        // aggFunc: getAggregateFunction(footerAggregate),
        // sortable: true,
        // field: field,
        // type: "text",
        // suppressAutoSize: suppressAutoResize ?? false


    return {
        field: field,
        headerName: headerName,
        cellRenderer: MemoCellRendererV2,
        comparator: memoTextComparator,
        // cellRendererParams: {
        //     // eslint-disable-next-line no-new-func
        //     //cellDataGetter: new Function("params", `return params.data?.${field};`) as (params: IStandardCellRendererParams) => StandardCellData,
        //     cellDataGetter: (params: BaseColDefParams) => {
        //         return params.data?.[field];
        //     }
        // },
        cellStyle: styler,
        // // eslint-disable-next-line no-new-func
        // valueGetter: new Function("params", `return params.data?.${field}?.displayText;`) as (params: ValueGetterParams) => string,
        // valueGetter: (params: ValueGetterParams) => {
        //     return params.data?.[field]?.displayText
        // },

        suppressSizeToFit: true,
        //Shortcuts column doesn't size properly with this included. Behavior was changes with AG Grid v35
        suppressAutoSize: true,

        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,

        // sort: initialSort ?? null,
        // sortIndex: initialSortIndex ?? null,
        //initialSort: initialSort,
        //initialSortIndex: initialSortIndex,
        //pinned: pinned ?? null,
        //pinned: initialPinned,
        initialPinned: initialPinned,
        lockPinned: lockPinned,
        lockPosition: lockPosition,
        initialWidth: 200,
        width: 200,
        //lockVisible: lockVisible,
        sortable: true,
        type: "text",

    };
}


export function createShortcutsColumn({
    field,
    headerName,
    initialPinned,
    lockPinned,
    lockPosition,
    disableNavigation
}: ColumnCreatorParams): ColDef {
    return {
        field: field,
        headerName: headerName,
        cellRenderer: ShortcutsCellRenderer,
        cellStyle: {
            'color': 'rgba(0, 0, 0, .54)'
        },
        cellRendererParams: (params: ICellRendererParams) => {
            // // eslint-disable-next-line no-new-func
            //cellDataGetter: new Function("params", `return params.data?.${field};`) as (params: IShortcutCellRendererParams) => ShortcutCellData,
            //cellDataGetter: new Function("params", `return params.data?.${field};`) as (params: IShortcutCellRendererParams) => IRoute[],

            return {
                ...params,
                disableNavigation: disableNavigation
                //onFunctionIconClicked: onFunctionIconClicked
            };
        },
        // This doesn't do anything other than eliminate a console warning:
        // AG Grid: Cell data type is "object" but no Value Formatter has been provided. Please either provide an object data type definition with a Value Formatter, or set "colDef.valueFormatter" 
        valueFormatter: () => {
            return "";
        },
        filter: undefined,
        //pinned: pinned ?? null,
        initialPinned: initialPinned,
        //pinned: initialPinned,
        lockPinned: lockPinned,
        lockPosition: lockPosition,

        sortable: false,
        resizable: true,
        suppressSizeToFit: true,
        //Shortcuts column doesn't size properly with this included. Behavior was changes with AG Grid v35
        //suppressAutoSize: true,

        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,

        // TODO: This should likely be based on theme spacing
        // TODO: It could also be sized based on the number of icons. That
        // would be more difficult because I would have to look through all
        // rows.       
        // width: 136,
        // maxWidth: 136,
        // minWidth: 136,
        // suppressMenu: true,
    };
}

export function createSystemBalanceColumn({
    field,
    headerName
}: ColumnCreatorParams): ColDef {
    return {
        field: field,
        headerName: headerName,
        valueGetter: (params: ValueGetterParams) => {
            if (params.data) {
                const value = params.data.systemBalanceType;
                return value === 1
                    ? "Inlet"
                    : value === 2
                        ? "Outlet"
                        : "Other";
            }
        },
        rowGroup: true,
        hide: true,
        // sortable: false,
        // cellRenderer: "systemBalanceCellRenderer",
        //lockVisible: true,
        // columnsMenuParams: {contractColumnSelection: true}
    };
}

export function createSparklineColumn({
    field,
    headerName
}: ColumnCreatorParams): ColDef {
    return {
        field: field,
        headerName: headerName,
        cellRenderer: SparklineCellRenderer,
        valueGetter: () => "Not Supported",
        filter: undefined,
        sortable: false,
        resizable: false,
        suppressSizeToFit: true,
        suppressAutoSize: true,
    };
}

